import React, { JSX, useEffect, useRef } from "react";

export interface TabToggleOption<T extends string> {
  value: T;
  label: React.ReactNode;
  /** Shown but not pickable: no hover, no click. */
  disabled?: boolean;
}

/**
 * Borderless middot-style tab strip for switching a dialog's mode.
 *
 * The third of three segmented controls, and they are not interchangeable:
 * `SegmentedControl` is the compact inline variant used by column-format
 * pickers, `SegmentedTabs` is full-width and bordered, and this one is the quiet
 * unboxed style the search & replace redesign moved to deliberately — hierarchy
 * from type and space rather than boxes. Pick by the surrounding grammar.
 */
export function TabToggle<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  framed = false,
}: {
  value: T;
  options: readonly TabToggleOption<T>[];
  onChange: (v: T) => void;
  ariaLabel: string;
  /** The picked tab wears the access rows' lit role button: accent tint, soft shadow. */
  framed?: boolean;
}): JSX.Element {
  const activeRef = useRef<HTMLButtonElement>(null);
  const shown = useRef(value);
  // A pick made elsewhere (a graph, a link) can land on a tab scrolled out of a
  // narrow strip. Not on mount: a dialog must open where it opens.
  useEffect(() => {
    if (shown.current === value) return;
    shown.current = value;
    activeRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }, [value]);
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="inline-flex items-center item-height gap-2"
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            ref={active ? activeRef : undefined}
            type="button"
            onClick={() => onChange(opt.value)}
            disabled={opt.disabled}
            aria-pressed={active}
            className={`tab-option h-7 ${
              opt.disabled
                ? "text-fg-muted-semibold cursor-default"
                : active
                  ? `cursor-default ${framed ? "text-fg-semibold bg-accent-15 shadow-xs" : "tab-option-active"}`
                  : "cursor-pointer tab-option-inactive"
            }`}
            style={{ fontSize: "var(--font-size)" }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
